import { useQuery } from "@tanstack/react-query";
import { callApi } from "../utils/ApiCall";

/**
 * useProductImage
 * Authenticated fetch of a product/catalogue image streamed as a Blob.
 * Mirrors behaviour of useLogoImage (infinite cache; returns Blob directly).
 *
 * @param {string} productCode - The product code / SKU root used to form the image filename.
 * @param {boolean} enabled - Allow caller to toggle fetching (defaults true if productCode present).
 * @param {object} options - Optional settings (extension override, e.g. webp/png).
 * @returns React Query result object with `data` being a Blob (or undefined while loading).
 */
export const useProductImage = (productCode, enabled = true, options = {}) => {
  const {
    preferredFormats = ["png"], // order matters
    extension, // force single extension if provided
  } = options;

  // Normalise the productCode: callers sometimes pass codes that already include an extension
  // (e.g. "MCO108061.png"). We always append our own, so strip any existing known image extension first.
  const normalisedCode = (productCode || "").replace(
    /\.(webp|png|avif|jpe?g)$/i,
    "",
  );

  const formatsToTry = extension ? [extension] : preferredFormats;
  const acceptHeader =
    formatsToTry
      .map((f) =>
        f === "png" ? "image/png" : f === "webp" ? "image/webp" : `image/${f}`,
      )
      .join(",") + ",*/*";

  return useQuery({
    queryKey: ["productImage", normalisedCode, formatsToTry.join("|")],
    enabled: enabled && !!normalisedCode,
    queryFn: async () => {
      for (const ext of formatsToTry) {
        const urlPath = `product-images/${normalisedCode}.${ext}`;
        try {
          const response = await callApi({
            method: "get",
            url: urlPath,
            responseType: "blob",
            headers: { Accept: acceptHeader },
          });
          if (response?.data instanceof Blob && response.data.size > 0) {
            return response.data;
          }
        } catch (e) {
          // try next format
          continue;
        }
      }
      throw new Error(
        `No available image formats for product ${normalisedCode}`,
      );
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
